import every from "lodash/every";
import first from "lodash/first";
import isArray from "lodash/isArray";
import map from "lodash/map";
import toString from "lodash/toString";
import { utc } from "PFCore/helpers/date";
import { getProperties } from "PFCore/helpers/templates";
import useBookingCategories from "PFCore/hooks/use_booking_categories";
import { useBooleanToString } from "PFCore/hooks/use_boolean_to_string";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { useItemsEnumerate } from "PFCore/hooks/use_items_enumerate";
import { Template } from "PFTypes";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { ReadableAvailabilityRules } from "../readable_availability_rules";

export const useValueRenderer = () => {
  const { t } = useTranslation();
  const { formatDate, formatDateTime } = useDateFormatter();
  const enumerateItems = useItemsEnumerate();
  const { getBookingCategory } = useBookingCategories();
  const booleanToString = useBooleanToString();

  return {
    "custom_field": ({ value, separator = "\n" }) =>
      typeof value === "string"
        ? value
        : map(value, (data) =>
            data
              ? data.text || data.text === ""
                ? data.text
                : data.value || data.value === 0
                  ? data.value
                  : data
              : ""
          ).join(separator),
    "integer": ({ value }) => value.toString(),
    "toggle": ({ value, description }) => {
      const formattedValue = booleanToString(toString(value) === "true");
      return description ? `${description}: ${formattedValue}` : formattedValue;
    },
    "date": ({ value }) => formatDate(value),
    "date-time": ({ value }) => formatDateTime(utc(value)),
    "date-range": ({ value }) => `${formatDate(utc(value.start_date))} - ${formatDate(utc(value.end_date))}`,
    "date-range-with-current": ({ value }) =>
      `${formatDate(utc(value.start_date))} - ${formatDate(utc(value.end_date))}`,
    "availability": ({ value }) => <ReadableAvailabilityRules availability={value} />,
    "booking_category_id": ({ value }) => getBookingCategory(value)?.display_as || "",
    "checkboxes": ({ value }) => {
      const selectedValues = value.filter(({ value }) => value).map(({ id }) => t(id, { defaultValue: id }));

      return enumerateItems(selectedValues);
    }
  };
};

type UsePropertyRendererReturn = (
  template: Template,
  name: string,
  value: any,
  valueType: string | null,
  separator?: string
) => React.ReactElement | string;

export const usePropertyRenderer = (): UsePropertyRendererReturn => {
  const stringifyFunctions = useValueRenderer();

  return useCallback(
    (template, name, value, valueType = null, separator = "\n") => {
      const property = getProperties(template).find((prop) => prop.name === name);
      if (!property) {
        return "";
      }
      let { type } = property;
      const { description } = property;

      // 'custom_field' of the valueType 'date' and 'boolean' are a special case that our 'custom_field'
      //  stringify function can't properly handle. We want to format them using the
      //  'date'/'toggle' serailizer.
      if (type === "custom_field" && valueType === "date") {
        type = "date";
        const firstValue: any = first(value);
        value = firstValue?.value ?? "";
      }

      if (type === "custom_field" && valueType === "boolean") {
        type = "toggle";
        const firstValue: any = first(value);
        value = firstValue?.value ?? "";
      }

      if (type === "checkboxes" && every(value, ({ value }) => !value)) {
        return "";
      }

      const stringify = type ? stringifyFunctions[type] : null;
      const out = (stringify && stringify({ value, description, separator, name })) || String(value) || "";

      if (type === "link") {
        const index = (out as string).indexOf("://");
        return 0 >= index || index > 8 ? `http://${out}` : out; // let's add http if no link protocol provided
      } else if (isArray(out)) {
        return out.join(separator);
      } else {
        return out;
      }
    },
    [stringifyFunctions]
  );
};
